// Waypoint API service

export interface WaypointVersion {
  id: number;
  museum: number;
  museum_code: string;
  museum_name: string;
  floor: string;
  version: number;
  s3_url: string;
  file_data: any[];
  uploaded_by: string;
  uploaded_at: string;
  is_active: boolean;
}

export interface UploadWaypointResponse {
  message: string;
  waypoint_version: WaypointVersion;
  sync_status: string;
}

export interface RestoreWaypointResponse {
  message: string;
  waypoint_version: WaypointVersion;
  sync_status: string;
}

export interface PathCostVersion {
  id: number;
  museum: number;
  museum_code: string;
  museum_name: string;
  floor: string;
  version: number;
  s3_url: string;
  file_data: any;
  uploaded_by: string;
  uploaded_at: string;
  is_active: boolean;
}

export interface RestorePathCostResponse {
  message: string;
  path_cost_version: PathCostVersion;
  sync_status: string;
}

export const waypointService = {
  uploadToS3: async (waypointData: any[], museumCode: string, floor: string): Promise<UploadWaypointResponse> => {
    const token = localStorage.getItem('token');
    const response = await fetch('/api/museums/waypoints/upload/', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        waypoint_data: waypointData,
        museum_code: museumCode,
        floor: floor,
      }),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  },

  listVersions: async (museumCode: string, floor?: string): Promise<WaypointVersion[]> => {
    const token = localStorage.getItem('token');
    const params: any = { museum_code: museumCode };
    if (floor) {
      params.floor = floor;
    }
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(`/api/museums/waypoints/versions/?${queryString}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  },

  listPathCostVersions: async (museumCode: string, floor?: string): Promise<PathCostVersion[]> => {
    const token = localStorage.getItem('token');
    const params: any = { museum_code: museumCode };
    if (floor) {
      params.floor = floor;
    }
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(`/api/museums/path_costs/versions/?${queryString}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  },

  restoreVersion: async (versionId: number): Promise<RestoreWaypointResponse> => {
    const token = localStorage.getItem('token');
    const response = await fetch(`/api/museums/waypoints/restore/`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        version_id: versionId,
      }),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  },

  uploadPathCosts: async (pathCostData: any, museumCode: string, floor: string): Promise<any> => {
    const token = localStorage.getItem('token');
    const response = await fetch(`/api/museums/path_costs/upload/`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        path_cost_data: pathCostData,
        museum_code: museumCode,
        floor,
      }),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  },

  restorePathCostVersion: async (versionId: number): Promise<RestorePathCostResponse> => {
    const token = localStorage.getItem('token');
    const response = await fetch(`/api/museums/path_costs/restore/`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        version_id: versionId,
      }),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  },

  getLatestWaypoints: async (museumCode: string, floor?: string): Promise<any[] | null> => {
    try {
      const token = localStorage.getItem('token');
      const params: any = { museum_code: museumCode };
      if (floor) {
        params.floor = floor;
      }
      const queryString = new URLSearchParams(params).toString();
      const response = await fetch(`/api/museums/waypoints/versions/?${queryString}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Find the active version
      const versions = (await response.json()) as PathCostVersion[];
      const activeVersion = versions.find(v => v.is_active);

      return activeVersion?.file_data || null;
    } catch (error) {
      console.error('Error fetching latest waypoints:', error);
      return null;
    }
  },

  getLatestPathCosts: async (museumCode: string, floor?: string): Promise<any | null> => {
    try {
      const token = localStorage.getItem('token');
      const params: any = { museum_code: museumCode };
      if (floor) {
        params.floor = floor;
      }
      const queryString = new URLSearchParams(params).toString();
      const response = await fetch(`/api/museums/path_costs/versions/?${queryString}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Find the active version
      const versions = (await response.json()) as WaypointVersion[];
      const activeVersion = versions.find(v => v.is_active);

      return activeVersion?.file_data || null;
    } catch (error) {
      console.error('Error fetching latest path costs:', error);
      return null;
    }
  },
};
